// /agents/:id — fetches the agent, hands it to AgentForm in edit mode.
// /agents/new wires AgentForm in create mode (no fetch).
//
// The edit page is tabbed (Playground / Settings / Knowledge / Install)
// since Phase 5.5 added the in-dashboard Playground for non-tech onboarding.
// The default tab is Settings to preserve the prior single-page layout for
// returning operators; arriving via the sample-agent flow (`?tab=playground`)
// lands on the wow-moment surface immediately.

import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useApiClient } from '../../lib/providers.js';
import { agentsApi, agentQueryKey } from '../../lib/api/agents.js';
import { Card, CardContent } from '../../components/ui/card.js';
import { ErrorRetry } from '../../components/ui/error-retry.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { Skeleton } from '../../components/ui/skeleton.js';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs.js';
import { AgentForm } from './form.js';
import { WidgetInstallCard } from './install-card.js';
import { KnowledgeCard } from './knowledge-card.js';
import { PlaygroundCard } from './playground.js';

type TabKey = 'playground' | 'settings' | 'knowledge' | 'install';

const TABS: readonly { key: TabKey; label: string }[] = [
  { key: 'playground', label: 'Playground' },
  { key: 'settings', label: 'Settings' },
  { key: 'knowledge', label: 'Knowledge' },
  { key: 'install', label: 'Install' },
];

function isTabKey(value: string | null): value is TabKey {
  return (
    value === 'playground' || value === 'settings' || value === 'knowledge' || value === 'install'
  );
}

export function AgentNewPage() {
  return (
    <div className="space-y-6">
      <Link
        to="/agents"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Back to agents
      </Link>
      <PageHeader
        title="New agent"
        description="Configure a new AI assistant: name, system prompt, model policy, tools, and budget."
      />
      <AgentForm mode="create" />
    </div>
  );
}

export function AgentEditPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const initialTab: TabKey = isTabKey(searchParams.get('tab'))
    ? (searchParams.get('tab') as TabKey)
    : 'settings';
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);

  const api = useApiClient();
  const agent = useQuery({
    queryKey: agentQueryKey(id),
    queryFn: () => agentsApi.get(api, id),
    enabled: id.length > 0,
  });

  if (agent.isLoading) {
    return (
      <Card>
        <CardContent className="space-y-3 p-6">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72" />
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }
  if (agent.isError) {
    return (
      <ErrorRetry
        title="Could not load agent"
        error={agent.error}
        onRetry={() => void agent.refetch()}
      />
    );
  }
  if (!agent.data) return <></>;

  const a = agent.data;
  return (
    <div className="space-y-6">
      <Link
        to="/agents"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Back to agents
      </Link>
      <PageHeader
        title={a.name}
        description={
          <>
            <code className="font-mono text-xs">{a.modelPolicy.provider}/{a.modelPolicy.model}</code>
            {' · '}
            <span>{a.tools.length} tools</span>
          </>
        }
      />
      <Tabs
        value={activeTab}
        onValueChange={(v) => {
          if (isTabKey(v)) setActiveTab(v);
        }}
      >
        <TabsList>
          {TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="playground">
          <PlaygroundCard agent={a} />
        </TabsContent>
        <TabsContent value="settings">
          <AgentForm mode="edit" initial={a} />
        </TabsContent>
        <TabsContent value="knowledge">
          <KnowledgeCard agent={a} />
        </TabsContent>
        <TabsContent value="install">
          <WidgetInstallCard agentId={a.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
